import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import {
  AddressBook,
  MagnifyingGlass,
  Phone,
  Envelope,
  Buildings,
  NotePencil,
  Tag,
  ChatCircle,
  X,
  Trash,
  Check,
  UserCircle,
  CaretRight
} from '@phosphor-icons/react';

export function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    if (selectedContact) {
      fetchContactConversations(selectedContact.id);
    }
  }, [selectedContact]);

  const fetchContacts = async (searchQuery) => {
    try {
      const res = await api.getContacts(searchQuery);
      setContacts(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContactConversations = async (contactId) => {
    try {
      const res = await api.getContactConversations(contactId);
      setConversations(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    fetchContacts(value || undefined);
  };

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setEditing(false);
    setEditData({
      name: contact.name || '',
      email: contact.email || '',
      company: contact.company || '',
      notes: contact.notes || '',
    });
  };

  const handleSave = async () => {
    if (!selectedContact) return;
    setSaving(true);
    try {
      const res = await api.updateContact(selectedContact.id, editData);
      setSelectedContact(res.data);
      setEditing(false);
      toast.success('Contacto actualizado');
      fetchContacts(search || undefined);
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedContact) return;
    try {
      await api.deleteContact(selectedContact.id);
      setSelectedContact(null);
      setShowDeleteModal(false);
      toast.success('Contacto eliminado');
      fetchContacts(search || undefined);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-[calc(100vh-64px)] flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-[#09090B]">
            Contactos
          </h1>
          <p className="text-[#52525B]">{contacts.length} contactos registrados</p>
        </div>
      </div>

      <div className="flex-1 flex gap-6 overflow-hidden">
        {/* Contact List */}
        <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col border border-[#E4E4E7] bg-white overflow-hidden">
          {/* Search */}
          <div className="p-4 border-b border-[#E4E4E7]">
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Buscar por nombre, teléfono, empresa..."
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
              />
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {contacts.length === 0 ? (
              <div className="text-center py-16">
                <AddressBook size={48} className="mx-auto text-[#A1A1AA] mb-4" />
                <h3 className="font-bold text-[#09090B] mb-1">Sin contactos</h3>
                <p className="text-sm text-[#52525B]">Los contactos se crean automáticamente cuando alguien te escribe por WhatsApp</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => handleSelectContact(contact)}
                  className={`w-full text-left p-4 border-b border-[#E4E4E7] hover:bg-[#F4F4F5] transition-colors ${
                    selectedContact?.id === contact.id ? 'bg-[#0047FF]/5 border-l-2 border-l-[#0047FF]' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center flex-shrink-0">
                      <UserCircle size={24} className="text-[#A1A1AA]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#09090B] truncate">
                        {contact.name || contact.phone}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-[#52525B]">
                        {contact.name && <span>{contact.phone}</span>}
                        {contact.company && (
                          <>
                            <span>·</span>
                            <span className="truncate">{contact.company}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-[#A1A1AA]">{formatDate(contact.last_message_at)}</p>
                      {contact.conversation_count > 0 && (
                        <span className="text-xs text-[#52525B]">{contact.conversation_count} conv.</span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Contact Detail */}
        {selectedContact ? (
          <div className="hidden lg:flex flex-1 flex-col border border-[#E4E4E7] bg-white overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0047FF]/10 flex items-center justify-center">
                  <UserCircle size={28} className="text-[#0047FF]" />
                </div>
                <div>
                  <h2 className="font-bold text-lg text-[#09090B]">
                    {selectedContact.name || selectedContact.phone}
                  </h2>
                  <p className="text-sm text-[#52525B]">{selectedContact.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!editing ? (
                  <button
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium border border-[#E4E4E7] rounded-xl hover:bg-[#F4F4F5]"
                  >
                    <NotePencil size={16} /> Editar
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setEditing(false)}
                      className="px-3 py-2 text-sm font-medium border border-[#E4E4E7] rounded-xl hover:bg-[#F4F4F5]"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium bg-[#0047FF] text-white rounded-xl hover:bg-[#0035CC] disabled:opacity-50"
                    >
                      <Check size={16} /> {saving ? 'Guardando...' : 'Guardar'}
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="p-2 text-[#A1A1AA] hover:text-red-500 hover:bg-red-50"
                  title="Eliminar contacto"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Info Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-1 font-mono text-xs uppercase text-[#52525B] mb-1">
                    <UserCircle size={14} /> Nombre
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      placeholder="Nombre del contacto"
                      className="w-full px-3 py-2 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
                    />
                  ) : (
                    <p className="text-sm text-[#09090B]">{selectedContact.name || <span className="text-[#A1A1AA]">Sin nombre</span>}</p>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-1 font-mono text-xs uppercase text-[#52525B] mb-1">
                    <Phone size={14} /> Teléfono
                  </label>
                  <p className="text-sm text-[#09090B] font-mono">{selectedContact.phone}</p>
                </div>
                <div>
                  <label className="flex items-center gap-1 font-mono text-xs uppercase text-[#52525B] mb-1">
                    <Envelope size={14} /> Email
                  </label>
                  {editing ? (
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      placeholder="correo@ejemplo.com"
                      className="w-full px-3 py-2 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
                    />
                  ) : (
                    <p className="text-sm text-[#09090B]">{selectedContact.email || <span className="text-[#A1A1AA]">-</span>}</p>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-1 font-mono text-xs uppercase text-[#52525B] mb-1">
                    <Buildings size={14} /> Empresa
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editData.company}
                      onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                      placeholder="Nombre de la empresa"
                      className="w-full px-3 py-2 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
                    />
                  ) : (
                    <p className="text-sm text-[#09090B]">{selectedContact.company || <span className="text-[#A1A1AA]">-</span>}</p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="flex items-center gap-1 font-mono text-xs uppercase text-[#52525B] mb-1">
                  <NotePencil size={14} /> Notas
                </label>
                {editing ? (
                  <textarea
                    value={editData.notes}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    placeholder="Agrega notas sobre este contacto..."
                    rows={4}
                    className="w-full px-3 py-2 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
                  />
                ) : (
                  <p className="text-sm text-[#09090B] whitespace-pre-wrap">
                    {selectedContact.notes || <span className="text-[#A1A1AA]">Sin notas</span>}
                  </p>
                )}
              </div>

              {/* Tags */}
              {selectedContact.tags?.length > 0 && (
                <div>
                  <label className="flex items-center gap-1 font-mono text-xs uppercase text-[#52525B] mb-2">
                    <Tag size={14} /> Etiquetas
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedContact.tags.map((tag) => (
                      <span key={tag} className="px-2 py-1 text-xs bg-[#F4F4F5] text-[#52525B]">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-xs text-[#A1A1AA]">
                <div>Primer mensaje: {formatDate(selectedContact.first_message_at)}</div>
                <div>Último mensaje: {formatDate(selectedContact.last_message_at)}</div>
                <div>Conversaciones: {selectedContact.conversation_count}</div>
              </div>

              {/* Conversations */}
              <div>
                <h3 className="font-bold text-sm text-[#09090B] mb-3 flex items-center gap-1">
                  <ChatCircle size={16} /> Conversaciones
                </h3>
                {conversations.length === 0 ? (
                  <p className="text-sm text-[#A1A1AA]">Sin conversaciones</p>
                ) : (
                  <div className="space-y-2">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => navigate(`/dashboard/inbox?conversation=${conv.id}`)}
                        className="w-full text-left p-3 border border-[#E4E4E7] rounded-xl hover:border-[#0047FF] transition-colors flex items-center justify-between"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-[#09090B] truncate">{conv.last_message || 'Sin mensajes'}</p>
                          <p className="text-xs text-[#A1A1AA]">{formatDate(conv.last_message_at)}</p>
                        </div>
                        <CaretRight size={16} className="text-[#A1A1AA] flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="hidden lg:flex flex-1 items-center justify-center bg-[#F4F4F5] border border-[#E4E4E7]">
            <div className="text-center">
              <AddressBook size={48} className="mx-auto text-[#A1A1AA] mb-4" />
              <p className="text-[#52525B]">Selecciona un contacto para ver su información</p>
            </div>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#E4E4E7]">
              <h3 className="font-bold text-lg text-[#09090B]">Eliminar contacto</h3>
            </div>
            <div className="p-6">
              <p className="text-[#52525B]">
                ¿Eliminar a <strong>{selectedContact.name || selectedContact.phone}</strong>? Las conversaciones se mantendrán.
              </p>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-xl font-medium hover:bg-[#F4F4F5]">
                Cancelar
              </button>
              <button onClick={handleDelete} className="flex-1 py-2 bg-red-500 text-white font-medium hover:bg-red-600">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
